/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    Form,
    FormValidator,
    FormLayout,
    isValid,
    useExternalFormApi,
    NoLabelGroup,
    LinkButton,
    Separator,
} from '@veeam-vspc/components';
import { usePageWizardStore } from '@veeam-vspc/components/src/PageWizard/hooks/use-page-wizard-store';
import { capitalize, formatStr } from '@veeam-vspc/core';

import type { WizardStep, WizardStepData } from '@veeam-vspc/components';

import { StepLayout } from 'components/layouts/StepLayout';
import { useLang } from 'views/providers/LangProvider/hooks';
import settingsIcon from 'images/actions/settings.svg';
import { PerMonthField } from 'components/controls/PerMonthField';
import { FormNote } from 'components/controls/FormNote';
import { PerBytesField } from 'components/controls/PerBytesField';
import { Microsoft365Advanced } from './components/Microsoft365Advanced';

import type { SubscriptionPlanModel } from 'core/interfaces';

export const stepValidate = (data: SubscriptionPlanModel) => {
    const validator = new FormValidator(data);

    validator
        .validate('vbM365ProtectedUserPrice')
        .number()
        .min(0)
        .max(1000000);
    validator
        .validate('vbM365BackupStorageSpacePrice')
        .number()
        .min(0)
        .max(1000000);
    validator
        .validate('vbM365ArchiveStorageSpacePrice')
        .number()
        .min(0)
        .max(1000000);

    return validator.result();
};

export const getMicrosoft365Step = (title: string): WizardStep<SubscriptionPlanModel> => ({
    title,
    validate: ({ data }) => isValid(stepValidate, data),
    render: data => <Microsoft365Step {...data} />,
});

const Microsoft365Step = (props: WizardStepData<SubscriptionPlanModel>) => {
    const lang = useLang();
    const { data, validationState, onDataChange } = props;
    const formApi = useExternalFormApi();
    const wizardContext = usePageWizardStore();
    const showPanel = () => wizardContext.openPanel(hide => <Microsoft365Advanced {...props} hide={hide} />);
    const currencyPerUser = formatStr(lang.CURRENCY_PER_USER, data.currency);
    const currencyPer = formatStr(lang.CURRENCY_PER, data.currency);

    return (
        <StepLayout
            title={capitalize(lang.MICROSOFT_365_BACKUP)}
            description={lang.DEFINE_SUBSCRIPTION_PLAN_PRICE_FOR_MICROSOFT_365}
        >
            <Form
                value={data}
                validate={stepValidate}
                validationState={validationState}
                externalFormApi={formApi}
                onChange={onDataChange}
            >
                <FormLayout inlineLabel>
                    <PerMonthField
                        name='vbM365ProtectedUserPrice'
                        label={lang.PROTECTED_USER}
                        suffix={currencyPerUser}
                    />

                    <PerBytesField
                        label={`${lang.STANDARD_STORAGE}:`}
                        valueName={'vbM365BackupStorageSpacePrice'}
                        unitsName={'vbM365BackupStorageSpaceMultiplier'}
                        suffix={currencyPer}
                    />

                    <PerBytesField
                        label={`${lang.ARCHIVE_STORAGE}:`}
                        valueName={'vbM365ArchiveStorageSpacePrice'}
                        unitsName={'vbM365ArchiveStorageSpaceMultiplier'}
                        suffix={currencyPer}
                    />

                    <Separator />

                    <NoLabelGroup
                        content={(
                            <div>
                                <LinkButton
                                    onClick={showPanel}
                                    iconBefore={settingsIcon}
                                >
                                    {`${lang.ADVANCED_SETTINGS}...`}
                                </LinkButton>

                                <FormNote>{lang.IN_THE_ADVANCED_SETTINGS_YOU_CAN_CONFIGURE_THE_NUMBER_OF_USERS}</FormNote>
                            </div>
                        )}
                    />
                </FormLayout>
            </Form>
        </StepLayout>
    );
};
